import React, { useRef, useState, useEffect } from 'react';
import { inspections as api } from '../utils/api';
import { fmtDate } from '../utils/helpers';

// Vistoria de qualidade do pedido: laudo do inspetor (PDF, fotos), resultado,
// AQL, peças inspecionadas e defeitos. Fica em destaque na fase "Controle de
// Qualidade". Opcional: só alerta no embarque se a última estiver reprovada/pendente.
export const RESULTS = {
  pending:               { label: 'Pendente',               icon: '⏳', color: 'var(--gray-600)', bg: 'var(--gray-100)' },
  approved:              { label: 'Aprovado',               icon: '✅', color: '#276749', bg: '#F0FFF4' },
  approved_with_remarks: { label: 'Aprovado com ressalvas', icon: '⚠️', color: '#B7791F', bg: '#FFFAF0' },
  rejected:              { label: 'Reprovado',              icon: '❌', color: '#B91C1C', bg: '#FEF2F2' },
};
export const TYPES = { pre_shipment: 'Pré-embarque', during_production: 'Durante a produção', reinspection: 'Re-inspeção', other: 'Outra' };
const EMPTY = { inspection_type: 'pre_shipment', inspection_date: '', inspector: '', result: 'approved', aql: '', qty_inspected: '', defects: '', notes: '' };

// Situação da qualidade para liberar o embarque: a vistoria mais recente manda
export function qualityGate(list) {
  const last = (list || [])[0];
  if (!last) return { ok: false, reason: 'none' };
  if (last.result === 'approved' || last.result === 'approved_with_remarks') return { ok: true, last };
  return { ok: false, reason: last.result, last };
}

export default function QualityInspections({ quotation: q, onRefresh, toast, highlight }) {
  const [list, setList] = useState(q.inspections || []);
  const [form, setForm] = useState(null);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const addRef = useRef();
  const [addTo, setAddTo] = useState(null);
  useEffect(() => { setList(q.inspections || []); }, [q.inspections]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const done = rows => { setList(rows); onRefresh?.(); };

  async function save() {
        setSaving(true);
    try {
      const { id, ...fields } = form;
      const rows = id ? await api.update(id, fields) : await api.create(q.id, fields, files);
      done(rows); setForm(null); setFiles([]);
      toast?.(id ? 'Vistoria atualizada' : 'Laudo de qualidade anexado', 'success');
    } catch (e) {
      toast?.(e.response?.status === 413 ? 'Arquivo grande demais para o servidor' : (e.response?.data?.error || 'Erro ao salvar vistoria'), 'error');
    } finally { setSaving(false); }
  }
  async function act(fn, msg) {
    try { done(await fn()); if (msg) toast?.(msg, 'success'); } catch (e) { toast?.(e.response?.data?.error || 'Erro', 'error'); }
  }

  const lbl = { fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 3 };
  const gate = qualityGate(list);
  const warn = highlight && !gate.ok && gate.reason !== 'none';
  const border = warn ? '#FBD38D' : (highlight && gate.ok ? '#C6F6D5' : 'var(--gray-200)');

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16, border: `1.5px solid ${border}` }} data-testid="quality-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', flex: 1 }}>🔍 VISTORIA DE QUALIDADE</div>
        {warn && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#B7791F' }}>
            {gate.reason === 'rejected' ? '❌ Última vistoria reprovada — re-inspecionar antes de embarcar' : '⏳ Vistoria pendente — atualize o resultado antes de embarcar'}
          </span>
        )}
        <button type="button" className="btn btn-primary btn-sm" onClick={() => { setFiles([]); setForm({ ...EMPTY }); }}>📎 Anexar laudo</button>
      </div>

      {form && (
        <div style={{ padding: 14, background: 'var(--accent-light, #FAF5F6)', borderRadius: 'var(--radius)', marginBottom: 12 }} data-testid="quality-form">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <div><label style={lbl}>Resultado</label>
              <select value={form.result} aria-label="Resultado" onChange={e => set('result', e.target.value)}>
                {Object.entries(RESULTS).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select></div>
            <div><label style={lbl}>Tipo</label>
              <select value={form.inspection_type} aria-label="Tipo de vistoria" onChange={e => set('inspection_type', e.target.value)}>
                {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
            <div><label style={lbl}>Data da vistoria</label>
              <input type="date" value={form.inspection_date || ''} aria-label="Data da vistoria" onChange={e => set('inspection_date', e.target.value)} /></div>
            <div><label style={lbl}>Inspetor / empresa</label>
              <input value={form.inspector || ''} aria-label="Inspetor" placeholder="Ex.: QIMA, agente, equipe Charth" onChange={e => set('inspector', e.target.value)} /></div>
            <div><label style={lbl}>AQL</label>
              <input value={form.aql || ''} aria-label="AQL" placeholder="Ex.: 2.5 / 4.0" onChange={e => set('aql', e.target.value)} /></div>
            <div><label style={lbl}>Peças inspecionadas</label>
              <input type="number" min="0" value={form.qty_inspected ?? ''} aria-label="Peças inspecionadas" onChange={e => set('qty_inspected', e.target.value)} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Defeitos encontrados</label>
              <textarea value={form.defects || ''} aria-label="Defeitos" rows={2} placeholder="Ex.: linha solta em 3 peças, mancha em 1, medida fora na manga (M)…" onChange={e => set('defects', e.target.value)} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Observações / ação combinada</label>
              <input value={form.notes || ''} aria-label="Observações da vistoria" placeholder="Ex.: fornecedor vai retrabalhar as peças antes do embarque" onChange={e => set('notes', e.target.value)} /></div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            {!form.id && (
              <>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>📎 Laudo e fotos</button>
                <input ref={fileRef} type="file" multiple accept=".pdf,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg,.webp,.heic" style={{ display: 'none' }}
                  data-testid="quality-files" onChange={e => { setFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
                <span style={{ fontSize: 12, color: files.length ? 'var(--gray-700)' : 'var(--gray-400)' }}>
                  {files.length ? files.map(f => f.name).join(', ') : 'PDF do laudo, fotos dos defeitos…'}
                </span>
              </>
            )}
            <div style={{ flex: 1 }} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setForm(null)} disabled={saving}>Cancelar</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? '⏳' : '💾 Salvar vistoria'}</button>
          </div>
        </div>
      )}

      {list.length === 0 && !form && (
        <div style={{ fontSize: 13, color: 'var(--gray-400)', padding: '6px 0' }}>Nenhuma vistoria registrada (opcional — anexe o laudo quando houver).</div>
      )}
      {list.map(i => {
        const r = RESULTS[i.result] || RESULTS.pending;
        return (
          <div key={i.id} data-testid="quality-row" style={{ border: '1px solid var(--gray-100)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: r.bg, color: r.color }}>{r.icon} {r.label}</span>
              <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>
                {TYPES[i.inspection_type] || 'Vistoria'} · {i.inspection_date ? fmtDate(i.inspection_date) : 'sem data'}
                {i.inspector ? ` · ${i.inspector}` : ''}{i.aql ? ` · AQL ${i.aql}` : ''}{i.qty_inspected ? ` · ${i.qty_inspected} pçs` : ''}
              </span>
              <div style={{ flex: 1 }} />
              <button type="button" className="btn btn-ghost btn-sm btn-icon" title="Editar vistoria"
                onClick={() => setForm({ id: i.id, inspection_type: i.inspection_type, inspection_date: i.inspection_date || '', inspector: i.inspector || '',
                  result: i.result, aql: i.aql || '', qty_inspected: i.qty_inspected ?? '', defects: i.defects || '', notes: i.notes || '' })}>✏️</button>
              <button type="button" className="btn btn-ghost btn-sm btn-icon" title="Anexar mais arquivos" onClick={() => { setAddTo(i.id); addRef.current?.click(); }}>➕</button>
              <button type="button" className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--error)' }} title="Excluir vistoria"
                onClick={() => window.confirm('Excluir esta vistoria e os arquivos dela?') && act(() => api.remove(i.id), 'Vistoria excluída')}>🗑️</button>
            </div>
            {i.defects && <div style={{ fontSize: 12, color: 'var(--gray-700)', marginTop: 6 }}>🔎 {i.defects}</div>}
            {i.notes && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>💬 {i.notes}</div>}
            {(i.files || []).length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {i.files.map(f => (
                  <span key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 6, padding: '3px 8px', fontSize: 12 }}>
                    {f.file_type === 'image'
                      ? <a href={`/uploads/${f.filename}`} target="_blank" rel="noreferrer"><img src={`/uploads/${f.filename}`} alt={f.original_name} style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 3, verticalAlign: 'middle' }} /></a>
                      : <a href={`/uploads/${f.filename}`} target="_blank" rel="noreferrer">📄 {f.original_name}</a>}
                    <button type="button" title="Remover arquivo" style={{ background: 'none', border: 'none', color: 'var(--red,#dc3545)', cursor: 'pointer', padding: 0 }}
                      onClick={() => window.confirm(`Remover ${f.original_name}?`) && act(() => api.deleteFile(f.id))}>✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <input ref={addRef} type="file" multiple style={{ display: 'none' }} data-testid="quality-add-files"
        onChange={e => { const fs = Array.from(e.target.files || []); e.target.value = ''; if (addTo && fs.length) act(() => api.addFiles(addTo, fs), 'Arquivo(s) anexado(s)'); }} />
    </div>
  );
}
